import { z } from 'zod';

/**
 * Either a string:
 * ```
 * com.colt.unlimited.yearly.baseline
 * ```
 * or a map of ios and android:
 * ```ts
 * {
 *   ios: 'com.colt.unlimited.yearly.baseline',
 *   android: 'com.colt.unlimited.yearly.baseline:p1y',
 * }
 * ```
 */
export const preferredProductIds = z.string().or(
  z.object({
    ios: z.string(),
    android: z.string(),
  }),
);
export type PreferredProductIds = z.infer<typeof preferredProductIds>;

// Offer Popup Config
/**
 * Offer Popup Config
 * ! Any changes made to this MUST be backwards compatible.
 */
export const offerPopupConfig = z
  .object({
    timesShow: z.number(),
    minHoursBetweenShowing: z.number(),
    preferredProductIds: preferredProductIds.optional(),
    triggerAction: z.enum(['offer_applied', 'chapter_finish']).optional(),
    timerTitleString: z.string().optional(),
    descriptionString: z.string().optional(),
    footerString: z.string().optional(),
    titleString: z.string().optional(),
    subtitleString: z.string().optional(),
    buttonTitleString: z.string().optional(),
  })
  .passthrough();
export type OfferPopupConfig = z.infer<typeof offerPopupConfig>;

// Discovery Banner Config
export const discoveryBannerConfig = z
  .object({
    titleString: z.string(),
    descriptionString: z.string(),
    descriptionStringWhenNoTimer: z.string().optional(),
    preferredProductIds: preferredProductIds.optional(),
    /** @deprecated - use `preferredProduct` instead */
    productId: z.string().optional(),
  })
  .passthrough()
  .superRefine((data) => {
    // If legacy productId is present and preferredProduct is not, migrate it
    if ('productId' in data && !data.preferredProductIds) {
      data.preferredProductIds = data.productId;
    }
  });
export type DiscoveryBannerConfig = z.infer<typeof discoveryBannerConfig>;

// Paywall Config
export const paywallConfig = z.object({
  timerPrefixString: z.string().optional(),
  textPrefixWhenTimerHidden: z.string().optional(),
});
export type PaywallConfig = z.infer<typeof paywallConfig>;

export const stripeOfferConfig = z.object({
  urlQueryParams: z.record(z.string(), z.string()).optional(),
  promocode: z.string().optional(),
  productSku: z.string(),
});
export type StripeOfferConfig = z.infer<typeof stripeOfferConfig>;

// Discount Config Item
export const discountConfigItem = z
  .object({
    productIdentifier: z.string(),
    discountIdentifier: z.string(),
    stripeConfig: stripeOfferConfig.optional(),
    os: z.enum(['ios', 'android']).optional(),
  })
  .passthrough();
export type DiscountConfigItem = z.infer<typeof discountConfigItem>;

export const stripeDiscountConfigItem = z
  .object({
    sku: z.string(),
    config: stripeOfferConfig,
    os: z.enum(['ios', 'android']).optional(),
  })
  .passthrough();
export type StripeDiscountConfigItem = z.infer<typeof stripeDiscountConfigItem>;

export const dynamicPaywallDiscountItem = z.discriminatedUnion('type', [
  discountConfigItem.extend({ type: z.literal('revcat') }),
  stripeDiscountConfigItem.extend({ type: z.literal('stripe') }),
]);
export type DynamicPaywallDiscountItem = z.infer<typeof dynamicPaywallDiscountItem>;

export const discountsArray = z.array(dynamicPaywallDiscountItem);
export type DiscountsArray = z.infer<typeof discountsArray>;

// Reminder Config Item
const reminderConfigItem = z
  .object({
    id: z.string(),
    minutesAfterOfferApplied: z.number(),
    titleString: z.string(),
    bodyString: z.string(),
    data: z.record(z.string(), z.string()).optional(),
  })
  .passthrough();
export const reminderConfig = z.array(reminderConfigItem);
export type ReminderConfig = z.infer<typeof reminderConfig>;

// Image Config
export const imageConfig = z.object({
  paywall: z.string().optional(),
  discoveryBanner: z.string().optional(),
});
export type ImageConfig = z.infer<typeof imageConfig>;

// Iterable config
export const iterableConfig = z.object({
  workflowId: z.number(),
  dataFields: z.record(z.string(), z.unknown()).optional(),
});
export type IterableConfig = z.infer<typeof iterableConfig>;

// Offer Phase
export const offerPhase = z.object({
  id: z.string(),
  dateRange: z.object({
    startDaysFromExpiration: z.number(),
    endDaysFromExpiration: z.number(),
  }),
  popupConfig: offerPopupConfig.optional(),
  images: imageConfig.optional(),
  discounts: discountsArray.optional(),
  discoveryBannerConfig: discoveryBannerConfig.optional(),
  paywallConfig: paywallConfig.optional(),
});
export type OfferPhase = z.infer<typeof offerPhase>;

// Winback Offer Data
export const dynamicPaywallWinbackOfferData = z.object({
  type: z.literal('winback'),
  // negative value means days before expiration. And positive value means days after expiration
  offerPhases: z.array(offerPhase),
  popupConfig: offerPopupConfig.optional(),
  images: imageConfig.optional(),
  discoveryBannerConfig: discoveryBannerConfig.optional(),
  paywallConfig: paywallConfig.optional(),
  showTimerWithHourRemaining: z.number().optional(),
  reminderConfig: reminderConfig.optional(),
  version: z.number().optional(),
  iterableConfig: iterableConfig.optional(),
  stripeWebBaseUrl: z.string().optional(),
});
export type DynamicPaywallWinbackOfferData = z.infer<typeof dynamicPaywallWinbackOfferData>;

// Golden Hour Config
export const goldenHourConfig = z.object({
  minChaptersRead: z.number(),
  maxChaptersRead: z.number(),
  minutesSinceInstallRequired: z.number(),
});
export type GoldenHourConfig = z.infer<typeof goldenHourConfig>;

// Normal Offer Data
export const dynamicPaywallNormalOfferData = z
  .object({
    type: z.literal('revcat_offering'),
    value: z.string(),
    ttl: z.number(),
    showTimerWithHourRemaining: z.number().optional(),
    discounts: discountsArray.optional(),
    version: z.number(),
    popupConfig: offerPopupConfig.optional(),
    images: imageConfig.optional(),
    allowCustomProducts: z.boolean().optional(),
    discoveryBannerConfig: discoveryBannerConfig.optional(),
    paywallConfig: paywallConfig.optional(),
    reminderConfig: reminderConfig.optional(),
    iterableConfig: iterableConfig.optional(),
    stripeWebBaseUrl: z.string().optional(),
    goldenHourConfig: goldenHourConfig.optional(),
    discountedProductId: z.string().optional(),
    theProductToReplaceItWith: z.string().optional(),
  })
  .passthrough();
export type DynamicPaywallNormalOfferData = z.infer<typeof dynamicPaywallNormalOfferData>;

export const dynamicPaywallOfferData = z.discriminatedUnion('type', [
  dynamicPaywallNormalOfferData,
  dynamicPaywallWinbackOfferData,
]);
export type DynamicPaywallOfferData = z.infer<typeof dynamicPaywallOfferData>;

export const dynamicPaywallOfferMetadata = z.object({
  key: z.string(),
  friendly_name: z.string(),
  isActive: z.boolean(),
  description: z.string(),
});

export const dynamicPaywallOffer = dynamicPaywallOfferMetadata.extend({
  offerData: dynamicPaywallOfferData,
});
export type DynamicPaywallOffer = z.infer<typeof dynamicPaywallOffer>;

export const testDynamicPaywallV2Definition = z.array(dynamicPaywallOffer);
export type TestDynamicPaywallV2Definition = z.infer<typeof testDynamicPaywallV2Definition>;

/** Golden Hour revcat discount: points (discountProductIdentifier) or subscription (discountIdentifier) */
export const goldenHourRevcatDiscountItem = z
  .object({
    productIdentifier: z.string(),
    discountIdentifier: z.string().optional(),
    discountProductIdentifier: z.string().optional(),
    os: z.enum(['ios', 'android']).optional(),
    type: z.literal('revcat'),
  })
  .passthrough();

/** Golden Hour stripe discount: web checkout with promocode */
export const goldenHourStripeDiscountItem = stripeDiscountConfigItem.extend({
  type: z.literal('stripe'),
});

/** Golden Hour discount: revcat or stripe */
export const goldenHourDiscountItem = z.discriminatedUnion('type', [
  goldenHourRevcatDiscountItem,
  goldenHourStripeDiscountItem,
]);
export type GoldenHourDiscountItem = z.infer<typeof goldenHourDiscountItem>;

/** Golden Hour offer data (revcat_offering) */
export const goldenHourOfferData = z
  .object({
    type: z.literal('revcat_offering'),
    value: z.string(),
    ttl: z.number(),
    showTimerWithHourRemaining: z.number().optional(),
    discounts: z.array(goldenHourDiscountItem).optional(),
    version: z.number(),
    popupConfig: offerPopupConfig.optional(),
    images: imageConfig.optional(),
    allowCustomProducts: z.boolean().optional(),
    discoveryBannerConfig: discoveryBannerConfig.optional(),
    paywallConfig: paywallConfig.optional(),
  })
  .passthrough();
export type GoldenHourOfferData = z.infer<typeof goldenHourOfferData>;

/** Golden Hour offer: full format with offerData */
export const goldenHourOfferFull = z.object({
  key: z.string(),
  offer_id: z.string().optional(),
  friendly_name: z.string(),
  isActive: z.boolean(),
  description: z.string(),
  offerData: goldenHourOfferData,
});
export type GoldenHourOfferFull = z.infer<typeof goldenHourOfferFull>;

export const iterableDynamicPaywallBody = z.object({
  userId: z.string(),
  baseAnnualProductId: z.string(),
  offer: dynamicPaywallOffer,
  source: z.string(),
});
export type IterableDynamicPaywallBody = z.infer<typeof iterableDynamicPaywallBody>;

/**
 * Iterable adds `userId` and `baseAnnualProductId` fields to the payload body.
 */
export type IterableDynamicPaywallBodyWithoutExtraIterableFields = Omit<
  IterableDynamicPaywallBody,
  'userId' | 'baseAnnualProductId'
>;

/**
 * This is only used in Vulcan to build winback DP offers
 */
export const dynamicPaywallWinbackOffer = dynamicPaywallOfferMetadata.extend({
  offerData: dynamicPaywallWinbackOfferData,
});
export type DynamicPaywallWinbackOffer = z.infer<typeof dynamicPaywallWinbackOffer>;

/**
 * This is only used in Vulcan to build normal DP offers
 */
export const dynamicPaywallNormalOffer = dynamicPaywallOfferMetadata.extend({
  offerData: dynamicPaywallNormalOfferData,
});
export type DynamicPaywallNormalOffer = z.infer<typeof dynamicPaywallNormalOffer>;
